import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { MANAGEMENT_URL } from "@/config/env";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthCard } from "@/components/AuthCard";

// #216 (D2b): First-Run-Setup. Wenn das System noch keinen Admin hat, legt der
// Operator hier den ersten an. Response = Login-Token (gleiche Form wie /auth/login)
// → wir loggen direkt ein.
interface SetupResponse {
  accessToken: string;
  expiresAt: string;
  user: { id: string; email: string; role: string };
}

function isValidSetupResponse(value: unknown): value is SetupResponse {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.accessToken !== "string" || v.accessToken.length === 0) return false;
  if (typeof v.expiresAt !== "string") return false;
  if (!Number.isFinite(new Date(v.expiresAt).getTime())) return false;
  if (typeof v.user !== "object" || v.user === null) return false;
  const u = v.user as Record<string, unknown>;
  return typeof u.id === "string" && typeof u.email === "string" && typeof u.role === "string";
}

export function Setup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);

  const { setSession } = useAuth();
  const navigate = useNavigate();

  // Schon initialisiert? Dann gibt es hier nichts zu tun → ab zum Login.
  useEffect(() => {
    let active = true;
    fetch(`${MANAGEMENT_URL}/auth/status`)
      .then((r) => r.json())
      .then((d: unknown) => {
        if (!active) return;
        const initialized = typeof d === "object" && d !== null && (d as { initialized?: boolean }).initialized;
        if (initialized) navigate("/login", { replace: true });
        else setChecking(false);
      })
      .catch(() => {
        if (active) setChecking(false); // Master nicht erreichbar → Form trotzdem zeigen
      });
    return () => {
      active = false;
    };
  }, [navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${MANAGEMENT_URL}/auth/bootstrap-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.status === 409) {
        setError("System ist bereits eingerichtet — bitte einloggen.");
        setTimeout(() => navigate("/login", { replace: true }), 1500);
        return;
      }
      if (res.status === 400) {
        setError("Ungültige Eingabe — Email-Format und Passwort (min. 8 Zeichen) prüfen.");
        return;
      }
      if (!res.ok) {
        setError(`Einrichtung fehlgeschlagen (HTTP ${res.status})`);
        return;
      }

      const data: unknown = await res.json();
      if (!isValidSetupResponse(data)) {
        setError("Antwort-Schema ungültig (Server-Bug?)");
        return;
      }
      const expiresAtMs = new Date(data.expiresAt).getTime();
      setSession({ accessToken: data.accessToken, expiresAt: expiresAtMs, user: data.user });
      navigate("/", { replace: true });
    } catch (err) {
      setError(`Server nicht erreichbar: ${(err as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center text-text3" data-testid="setup-checking">
        Prüfe System-Status…
      </div>
    );
  }

  return (
    <AuthCard
      title="Elyvers"
      description="Ersteinrichtung — lege den ersten Plattform-Admin an. Danach ist diese Seite gesperrt."
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="setup-form">
            <div className="space-y-2">
              <Label htmlFor="email">Admin-Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passwort (min. 8 Zeichen)</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isSubmitting}
                data-testid="password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Passwort bestätigen</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                disabled={isSubmitting}
                data-testid="confirm-input"
              />
            </div>
            {error && (
              <div
                className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger"
                role="alert"
                data-testid="setup-error"
              >
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="submit-button">
              {isSubmitting ? "Richte ein…" : "Admin anlegen & einloggen"}
            </Button>
          </form>
    </AuthCard>
  );
}
